import { Button, Table } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import ToggleSwitch from "src/components/ui/switch";
import GlobalContext from "src/context/global-context";
import {
  APISomethingWrongMsg,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { getErrorMessage } from "src/lib/utils";
import client from "../../api";
import { ColumnsType } from "antd/es/table";
import moment from "moment";
import debounce from "lodash.debounce";

const ServiceMaintenance = () => {
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();

  const { canView, canList, canEdit } = usePermissions(
    permissionConstants.SERVICE_MAINTENANCES
  );
  const canViewMaintenance = canView || canList;

  const [dataSource, setDataSource] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currencyModalOpen, setCurrencyModalOpen] = useState(false);
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [currencyError, setCurrencyError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  // Global maintenance state
  const [isGlobalMaintenance, setIsGlobalMaintenance] =
    useState<boolean>(false);
  const [isCardMaintenance, setIsCardMaintenance] = useState<boolean>(false);
  const [globalConfirmOpen, setGlobalConfirmOpen] = useState<boolean>(false);
  const [pendingGlobalState, setPendingGlobalState] = useState<boolean>(false);
  const [pendingMaintenanceId, setPendingMaintenanceId] = useState<
    number | null
  >(null);

  const ALL_CURRENCIES = ["CNY", "EUR", "GBP", "GEL", "RUB", "UAH", "USD"];

  const { mutate: updateGlobalMaintenance, isLoading: isGlobalUpdating } =
    useMutation(
      (params: { id: number; isMaintenance: boolean }) =>
        client.serviceMaintenance.updateGlobalMaintenance(params),
      {
        onSuccess: (data) => {
          setAlertText(data.message);
          setIsAlertOpened(true);
          // setIsGlobalMaintenance(pendingGlobalState);
          // Refresh the list to reflect changes
          getGloabalMaintenance();
          getTemplatesList({ page: currentPage, limit: pageSize });
        },
        onError: (error: any) => {
          setAlertText(
            error.response
              ? getErrorMessage(error.response.data)
              : APISomethingWrongMsg,
          );
          setIsAlertOpened(true);
        },
      },
    );

  const { mutate: getGloabalMaintenance, isLoading: isGlobalFetching } =
    useMutation(client.serviceMaintenance.getGloabalMaintenance, {
      onSuccess: (data: any) => {
        const maintenanceSettings = Array.isArray(data?.data) ? data.data : [];
        const globalMaintenance = maintenanceSettings.find(
          (item: any) => item.key_name === "maintenance_mode",
        );
        const cardMaintenance = maintenanceSettings.find(
          (item: any) => item.key_name === "maintenance_cards",
        );
        setIsGlobalMaintenance(
          globalMaintenance?.value === true ||
            globalMaintenance?.value === "true",
        );
        setIsCardMaintenance(
          cardMaintenance?.value === true || cardMaintenance?.value === "true",
        );
      },

      onError: (error: any) => {
        if (error?.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else if (error?.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }

        setIsAlertOpened(true);
      },
    });

  const handleMaintenanceToggle = (id: number, newValue: boolean) => {
    if (!canEdit) {
      setAlertText("You do not have permission to update maintenance settings");
      setIsAlertOpened(true);
      return;
    }
    setPendingMaintenanceId(id);
    setPendingGlobalState(newValue);
    setGlobalConfirmOpen(true);
  };

  const confirmMaintenance = () => {
    if (!canEdit) {
      setAlertText("You do not have permission to update maintenance settings");
      setIsAlertOpened(true);
      return;
    }
    if (pendingMaintenanceId === null) {
      return;
    }

    updateGlobalMaintenance({
      id: pendingMaintenanceId,
      isMaintenance: pendingGlobalState,
    });

    setGlobalConfirmOpen(false);
    setPendingMaintenanceId(null);
  };

  /** ---------------- Fetch Templates API ---------------- */
  const { mutate: getTemplatesList, isLoading } = useMutation(
    (params: { page: number; limit: number; q?: string }) =>
      client.serviceMaintenance.getAllServicesList(params),

    {
      onSuccess: (data) => {
        setDataSource(
          data.items.map((item: any) => ({
            ...item,
          })),
        );
        setTotalItems(data.totalcount || 0);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") setAlertText(NetworkDisconnectMsg);
        else if (error.response)
          setAlertText(getErrorMessage(error.response.data));
        else setAlertText(APISomethingWrongMsg);
        setIsAlertOpened(true);
      },
    },
  );

  const { mutate: updateService, isLoading: isUpdating } = useMutation(
    client.serviceMaintenance.updateService,
    {
      onSuccess: (data) => {
        setAlertText(data.message);
        setIsAlertOpened(true);
        getTemplatesList({ page: currentPage, limit: pageSize });
      },
      onError: (error: any) => {
        setAlertText(
          error.response
            ? getErrorMessage(error.response.data)
            : APISomethingWrongMsg,
        );
        setIsAlertOpened(true);
      },
    },
  );

  const debouncedSearch = React.useMemo(
    () =>
      debounce((value: string) => {
        setCurrentPage(1); // reset page when searching
        setSearchText(value.trim());
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (!isMounted || !canViewMaintenance) return;
    getGloabalMaintenance();
  }, [isMounted, canViewMaintenance]);

  useEffect(() => {
    if (!isMounted || !canViewMaintenance) return;
    getTemplatesList({
      page: currentPage,
      limit: pageSize,
      q: searchText || undefined,
    });
  }, [isMounted, canViewMaintenance, currentPage, pageSize, searchText]);

  const columns: ColumnsType<any> = [
    {
      title: "Module name",
      dataIndex: "module_name",
      key: "module_name",
      render: (value) => <span className="text-primary">{value}</span>,
    },

    { title: "Service key", dataIndex: "service_key", key: "service_key" },

    {
      title: "Currencies",
      dataIndex: "currency",
      key: "currency",
      render: (currencies: string | null) => (
        <span>{currencies ? currencies.replaceAll(",", ", ") : "_"}</span>
      ),
    },

    { title: "Updated by", dataIndex: "updated_by", key: "updated_by" },
    {
      title: "Updated at",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {/* {moment.unix(value).format("YYYY-MM-DD HH:mm:ss")} */}
            {moment.utc(value).local().format("YYYY-MM-DD HH:mm:ss")}{" "}
          </p>
        </div>
      ),
    },

    {
      title: "Maintenance",
      dataIndex: "is_maintenance",
      key: "is_maintenance",
      render: (is_maintenance, record) => {
        const isDisabled = !canEdit;
        return (
          <div
            className={`flex flex-row gap-2 items-center ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            title={!canEdit ? "Read only - No edit permission" : undefined}
          >
            <ToggleSwitch
              type={`${record.id}`}
              value={!!is_maintenance}
              disabled={isDisabled}
              onValueChanged={(_: any, newValue: boolean) => {
                if (!canEdit) return;
                // turning ON
                if (newValue) {
                  // ⭐ if service starts with mm_ → skip currency selection
                  if (record.service_key?.toLowerCase().startsWith("mm_")) {
                    updateService({
                      id: record?.id,
                      isMaintenance: true,
                      message: record?.message,
                      // currency: "", // or null if API supports
                    });
                    return;
                  }

                  // normal flow → open currency modal
                  setSelectedRecord(record);
                  setSelectedCurrencies(
                    record?.currency ? record.currency.split(",") : [],
                  );
                  setCurrencyModalOpen(true);
                  return;
                }

                // turning OFF → update directly
                updateService({
                  id: record.id,
                  isMaintenance: false,
                  message: record.message,
                  // currency: "",
                });
              }}
            />
          </div>
        );
      },
    },
  ];
  return (
    <div className="w-full h-full bg-gray">
      <>
        <div className="flex justify-between items-center p-2 sm:p-4">
          <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium">
            Maintenance services
          </p>
          <input
            type="text"
            placeholder="Search by module name"
            value={searchInput}
            onChange={(e) => {
              const value = e.target.value;
              setSearchInput(value); // update UI instantly
              debouncedSearch(value); // call API after delay
            }}
            className="border px-3 py-2 rounded-md w-[250px] focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {/* Global Maintenance Toggle */}
          {/* Maintenance Toggles */}
          <div className="flex items-center gap-3">
            {/* Global Maintenance */}
            <div className="flex items-center gap-2 border border-gray-900 rounded-lg px-4 py-2 bg-gray-50 shadow-sm">
              <span className="text-sm font-medium font-manrope text-gray-600 whitespace-nowrap">
                Global Maintenance
              </span>

              <button
                onClick={() => handleMaintenanceToggle(2, !isGlobalMaintenance)}
                disabled={!canEdit || isGlobalUpdating || isGlobalFetching}
                title={!canEdit ? "Read only - No edit permission" : undefined}
                className={`
        relative inline-flex h-6 w-11 items-center rounded-full
        transition-colors duration-200 ease-in-out
        focus:outline-none
        ${!canEdit || isGlobalUpdating || isGlobalFetching ? "opacity-50 cursor-not-allowed" : ""}
        ${isGlobalMaintenance ? "bg-red-500" : "bg-gray-900"}
      `}
                aria-label="Toggle global maintenance"
              >
                <span
                  className={`
          inline-block h-4 w-4 transform rounded-full
          bg-white shadow-md
          transition-transform duration-200 ease-in-out
          ${isGlobalMaintenance ? "translate-x-6" : "translate-x-1"}
        `}
                />
              </button>

              <span
                className={`
        text-xs font-semibold px-2 py-0.5 rounded-full
        ${
          isGlobalMaintenance
            ? "bg-red-100 text-red-600"
            : "bg-green-100 text-green-600"
        }
      `}
              >
                {isGlobalUpdating || isGlobalFetching
                  ? "..."
                  : isGlobalMaintenance
                    ? "ON"
                    : "OFF"}
              </span>
            </div>

            {/* Card Maintenance */}
            <div className="flex items-center gap-2 border border-gray-900 rounded-lg px-4 py-2 bg-gray-50 shadow-sm">
              <span className="text-sm font-medium font-manrope text-gray-600 whitespace-nowrap">
                Card Maintenance
              </span>

              <button
                onClick={() => handleMaintenanceToggle(3, !isCardMaintenance)}
                disabled={!canEdit || isGlobalUpdating || isGlobalFetching}
                title={!canEdit ? "Read only - No edit permission" : undefined}
                className={`
        relative inline-flex h-6 w-11 items-center rounded-full
        transition-colors duration-200 ease-in-out
        focus:outline-none
        ${!canEdit || isGlobalUpdating || isGlobalFetching ? "opacity-50 cursor-not-allowed" : ""}
        ${isCardMaintenance ? "bg-red-500" : "bg-gray-900"}
      `}
                aria-label="Toggle card maintenance"
              >
                <span
                  className={`
          inline-block h-4 w-4 transform rounded-full
          bg-white shadow-md
          transition-transform duration-200 ease-in-out
          ${isCardMaintenance ? "translate-x-6" : "translate-x-1"}
        `}
                />
              </button>

              <span
                className={`
        text-xs font-semibold px-2 py-0.5 rounded-full
        ${
          isCardMaintenance
            ? "bg-red-100 text-red-600"
            : "bg-green-100 text-green-600"
        }
      `}
              >
                {isGlobalUpdating || isGlobalFetching
                  ? "..."
                  : isCardMaintenance
                    ? "ON"
                    : "OFF"}
              </span>
            </div>
          </div>
        </div>
        {!canViewMaintenance ? (
          <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 mt-2 sm:mt-4 text-center shadow-sm">
            <p className="font-manrope text-[16px] font-semibold text-secondary">
              You do not have permission to view maintenance services.
            </p>
          </div>
        ) : (
          <div className="flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
            <Table
              loading={isLoading}
              showSorterTooltip={false}
              columns={columns}
              dataSource={dataSource}
              pagination={{
                total: totalItems,
                current: currentPage,
                onChange: (page, size) => setCurrentPage(page),
                position: ["bottomCenter"],
                showSizeChanger: false,
              }}
            />
          </div>
        )}
      </>
      {currencyModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white p-6 rounded-lg w-[400px]">
            <h3 className="text-lg font-semibold mb-4">
              Select currencies for maintenance
            </h3>

            <div className="flex flex-col gap-2">
              {ALL_CURRENCIES.map((currency) => (
                <label key={currency} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedCurrencies.includes(currency)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCurrencies((prev) => [...prev, currency]);
                        setCurrencyError(""); // ✅ clear error
                      } else {
                        setSelectedCurrencies((prev) =>
                          prev.filter((c) => c !== currency),
                        );
                      }
                    }}
                  />
                  {currency}
                </label>
              ))}
            </div>
            {currencyError && (
              <p className="text-red-500 text-sm mt-2">{currencyError}</p>
            )}
            <div className="flex justify-end gap-2 mt-6">
              <button
                className="px-4 py-2 border rounded"
                onClick={() => setCurrencyModalOpen(false)}
              >
                Cancel
              </button>

              <button
                className="px-4 py-2 rounded bg-blue-600 text-white"
                onClick={() => {
                  if (!canEdit) {
                    setAlertText("You do not have permission to update maintenance settings");
                    setIsAlertOpened(true);
                    return;
                  }
                  if (!selectedRecord) return;

                  if (selectedCurrencies.length === 0) {
                    setCurrencyError("Please select at least one currency");
                    return;
                  }

                  updateService({
                    id: selectedRecord.id,
                    isMaintenance: true,
                    message: selectedRecord.message,
                    currency: selectedCurrencies.join(","),
                  });

                  setCurrencyModalOpen(false);
                  setSelectedCurrencies([]);
                  setCurrencyError("");
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
      {globalConfirmOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white p-6 rounded-lg w-[420px] shadow-xl">
            <h3 className="text-lg font-semibold text-center mb-2">
              {pendingGlobalState
                ? `Enable ${
                    pendingMaintenanceId === 2 ? "Card" : "Global"
                  } Maintenance?`
                : `Disable ${
                    pendingMaintenanceId === 2 ? "Card" : "Global"
                  } Maintenance?`}
            </h3>

            <p className="text-sm text-gray-500 text-center mb-6">
              {pendingGlobalState
                ? pendingMaintenanceId === 2
                  ? "This will put card services into maintenance mode."
                  : "This will put all services into maintenance mode."
                : pendingMaintenanceId === 2
                  ? "This will restore card services from maintenance mode."
                  : "This will restore all services from maintenance mode."}
            </p>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => {
                  setGlobalConfirmOpen(false);
                  setPendingMaintenanceId(null);
                }}
              >
                Cancel
              </button>

              <button
                className={`px-4 py-2 rounded-md text-sm text-white ${
                  pendingGlobalState
                    ? "bg-red-500 hover:bg-red-600"
                    : "bg-green-500 hover:bg-green-600"
                }`}
                onClick={confirmMaintenance}
                disabled={isGlobalUpdating}
              >
                {pendingGlobalState ? "Yes, Enable" : "Yes, Disable"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceMaintenance;
